import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type ForwardedRef, type PropsWithChildren } from 'react';
import { Canvas, useFrame, type ThreeEvent } from '@react-three/fiber';
import { OrbitControls, Sky } from '@react-three/drei';
import * as THREE from 'three';
import type { PanDirection } from '@/components/ui/zoom-pan-controls';
import { checkWebGLCapability } from '@/lib/webgl-capability';
import {
  CAMERA_PRESETS,
  DOORS,
  FLOOR_BOUNDS,
  FURNITURE_ANCHORS,
  ROOMS,
  STRUCTURAL_WALLS,
  type CameraPreset,
  type DoorOpening,
  type FurnitureAnchor,
  type RoomDef,
  type RoomId,
  type WallSegment,
} from './woodsmoke-twin-data';

export interface WoodsmokeTwinCameraController {
  zoomIn: () => void;
  zoomOut: () => void;
  pan: (direction: PanDirection) => void;
  reset: () => void;
}

export interface WoodsmokeTwinSceneProps extends PropsWithChildren {
  cameraPreset: CameraPreset;
  selectedRoomId?: RoomId | null;
  onSelect: (room: RoomDef | null) => void;
  onHoverChange?: (room: RoomDef | null) => void;
}

type OrbitControlsHandle = {
  object: THREE.Camera & { position: THREE.Vector3 };
  target: THREE.Vector3;
  update: () => void;
  enabled: boolean;
};

type WallSpan = { from: number; to: number };

const WALL_COLORS: Record<WallSegment['material'], string> = {
  plaster: '#e6dfd4',
  stone: '#766451',
  glass: '#a9d8e6',
  balustrade: '#48585a',
};

function createWallGeometry(wall: WallSegment, from = 0, to = 1): THREE.BoxGeometry {
  const height = wall.height ?? 3.2;
  const thickness = wall.thickness ?? 0.22;
  const dx = wall.x2 - wall.x1;
  const dz = wall.z2 - wall.z1;
  const length = Math.hypot(dx, dz);
  const segmentLength = Math.max(length * (to - from), 0.01);
  return new THREE.BoxGeometry(segmentLength, height, thickness);
}

function wallPosition(wall: WallSegment, from = 0, to = 1): [number, number, number] {
  const height = wall.height ?? 3.2;
  const midpoint = (from + to) / 2;
  return [
    THREE.MathUtils.lerp(wall.x1, wall.x2, midpoint),
    height / 2,
    THREE.MathUtils.lerp(wall.z1, wall.z2, midpoint),
  ];
}

function wallRotation(wall: WallSegment): [number, number, number] {
  return [0, -Math.atan2(wall.z2 - wall.z1, wall.x2 - wall.x1), 0];
}

function openingSpan(wall: WallSegment, door: DoorOpening): WallSpan | null {
  const horizontal = Math.abs(wall.z2 - wall.z1) < 0.01;
  const vertical = Math.abs(wall.x2 - wall.x1) < 0.01;
  const length = horizontal ? Math.abs(wall.x2 - wall.x1) : Math.abs(wall.z2 - wall.z1);
  const coordinate = horizontal ? door.x : door.z;
  const start = horizontal ? Math.min(wall.x1, wall.x2) : Math.min(wall.z1, wall.z2);
  const fixedCoordinate = horizontal ? wall.z1 : wall.x1;
  const doorFixedCoordinate = horizontal ? door.z : door.x;

  if ((!horizontal && !vertical) || Math.abs(fixedCoordinate - doorFixedCoordinate) > 0.18) return null;

  const center = (coordinate - start) / length;
  const half = door.width / (2 * length);
  if (center + half <= 0 || center - half >= 1) return null;
  return { from: Math.max(0, center - half), to: Math.min(1, center + half) };
}

function spansForWall(wall: WallSegment): WallSpan[] {
  const openings = DOORS
    .map((door) => openingSpan(wall, door))
    .filter((span): span is WallSpan => span !== null)
    .sort((first, second) => first.from - second.from);
  const spans: WallSpan[] = [];
  let cursor = 0;

  for (const opening of openings) {
    if (opening.from > cursor) spans.push({ from: cursor, to: opening.from });
    cursor = Math.max(cursor, opening.to);
  }
  if (cursor < 1) spans.push({ from: cursor, to: 1 });
  return spans;
}

function WallSpanMesh({ wall, span }: { wall: WallSegment; span: WallSpan }) {
  const geometry = useMemo(() => createWallGeometry(wall, span.from, span.to), [wall, span.from, span.to]);
  const isGlass = wall.material === 'glass';

  useEffect(() => () => geometry.dispose(), [geometry]);

  return (
    <mesh
      geometry={geometry}
      position={wallPosition(wall, span.from, span.to)}
      rotation={wallRotation(wall)}
      castShadow={!isGlass}
      receiveShadow
    >
      <meshStandardMaterial
        color={WALL_COLORS[wall.material]}
        roughness={isGlass ? 0.12 : wall.material === 'stone' ? 0.92 : 0.78}
        metalness={isGlass ? 0.18 : 0}
        transparent={isGlass}
        opacity={isGlass ? 0.38 : 1}
      />
    </mesh>
  );
}

function StructuralWall({ wall }: { wall: WallSegment }) {
  const spans = useMemo(() => spansForWall(wall), [wall]);

  return <group>{spans.map((span, index) => <WallSpanMesh key={`${wall.id}-${index}`} wall={wall} span={span} />)}</group>;
}

function RoomFloor({ room, isSelected, onSelect, onHoverChange }: {
  room: RoomDef;
  isSelected: boolean;
  onSelect: (room: RoomDef) => void;
  onHoverChange?: (room: RoomDef | null) => void;
}) {
  const highlightRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (highlightRef.current && isSelected) {
      const material = highlightRef.current.material as THREE.MeshBasicMaterial;
      material.opacity = 0.14 + Math.sin(state.clock.elapsedTime * 3) * 0.05;
    }
  });

  return (
    <group>
      <mesh
        position={[room.x, 0.02, room.z]}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
        onClick={(event: ThreeEvent<MouseEvent>) => {
          event.stopPropagation();
          onSelect(room);
        }}
        onPointerOver={(event: ThreeEvent<PointerEvent>) => {
          event.stopPropagation();
          document.body.style.cursor = 'pointer';
          onHoverChange?.(room);
        }}
        onPointerOut={() => {
          document.body.style.cursor = 'default';
          onHoverChange?.(null);
        }}
      >
        <planeGeometry args={[room.width, room.depth]} />
        <meshStandardMaterial color={room.floorColor} roughness={room.id.includes('bath') || room.id.includes('powder') ? 0.42 : 0.82} metalness={0.03} />
      </mesh>
      <mesh ref={highlightRef} position={[room.x, 0.045, room.z]} rotation={[-Math.PI / 2, 0, 0]} visible={isSelected}>
        <planeGeometry args={[room.width - 0.24, room.depth - 0.24]} />
        <meshBasicMaterial color="#f7c873" transparent opacity={0.16} depthWrite={false} />
      </mesh>
    </group>
  );
}

function Bed({ anchor, bunk = false }: { anchor: FurnitureAnchor; bunk?: boolean }) {
  const width = anchor.width ?? (bunk ? 1.1 : 2.05);
  const depth = anchor.depth ?? (bunk ? 2.15 : 2.25);
  const levels = bunk ? [0.45, 1.38] : [0.48];

  return (
    <group position={[anchor.x, 0, anchor.z]} rotation={[0, anchor.rotationY ?? 0, 0]}>
      <mesh position={[0, 0.22, 0]} castShadow receiveShadow>
        <boxGeometry args={[width + 0.14, 0.28, depth + 0.14]} />
        <meshStandardMaterial color="#4f362a" roughness={0.68} />
      </mesh>
      {levels.map((height) => (
        <group key={height}>
          <mesh position={[0, height, 0]} castShadow receiveShadow>
            <boxGeometry args={[width, 0.22, depth]} />
            <meshStandardMaterial color="#ece8df" roughness={0.88} />
          </mesh>
          <mesh position={[0, height + 0.22, -depth * 0.2]} castShadow>
            <boxGeometry args={[width * 0.92, 0.16, depth * 0.5]} />
            <meshStandardMaterial color="#8eaaaf" roughness={0.9} />
          </mesh>
        </group>
      ))}
      {bunk && [-1, 1].flatMap((x) => [-1, 1].map((z) => (
        <mesh key={`${x}-${z}`} position={[x * (width / 2 + 0.03), 0.95, z * (depth / 2 + 0.03)]} castShadow>
          <cylinderGeometry args={[0.055, 0.055, 1.9, 8]} />
          <meshStandardMaterial color="#302b28" metalness={0.5} roughness={0.38} />
        </mesh>
      )))}
    </group>
  );
}

function Sofa({ anchor }: { anchor: FurnitureAnchor }) {
  const width = anchor.width ?? 3.3;
  const depth = anchor.depth ?? 1.15;

  return (
    <group position={[anchor.x, 0, anchor.z]} rotation={[0, anchor.rotationY ?? 0, 0]}>
      <mesh position={[0, 0.48, 0]} castShadow receiveShadow>
        <boxGeometry args={[width, 0.55, depth]} />
        <meshStandardMaterial color="#5e6c6f" roughness={0.92} />
      </mesh>
      <mesh position={[0, 1.02, depth * 0.36]} castShadow>
        <boxGeometry args={[width, 0.75, 0.22]} />
        <meshStandardMaterial color="#536165" roughness={0.92} />
      </mesh>
      {[-1, 1].map((side) => (
        <mesh key={side} position={[side * (width / 2 - 0.11), 0.72, 0]} castShadow>
          <boxGeometry args={[0.22, 0.5, depth]} />
          <meshStandardMaterial color="#536165" roughness={0.92} />
        </mesh>
      ))}
    </group>
  );
}

function DiningTable({ anchor }: { anchor: FurnitureAnchor }) {
  const width = anchor.width ?? 3.2;
  const depth = anchor.depth ?? 1.25;
  return (
    <group position={[anchor.x, 0, anchor.z]} rotation={[0, anchor.rotationY ?? 0, 0]}>
      <mesh position={[0, 0.83, 0]} castShadow receiveShadow>
        <boxGeometry args={[width, 0.14, depth]} />
        <meshStandardMaterial color="#79563a" roughness={0.56} />
      </mesh>
      {[-1, 1].flatMap((x) => [-1, 1].map((z) => (
        <mesh key={`${x}-${z}`} position={[x * (width / 2 - 0.22), 0.4, z * (depth / 2 - 0.18)]} castShadow>
          <boxGeometry args={[0.11, 0.8, 0.11]} />
          <meshStandardMaterial color="#493428" roughness={0.62} />
        </mesh>
      )))}
      {[-1, 1].flatMap((side) => [-0.82, 0, 0.82].map((offset) => (
        <group key={`${side}-${offset}`} position={[offset, 0, side * (depth / 2 + 0.46)]} rotation={[0, side > 0 ? Math.PI : 0, 0]}>
          <mesh position={[0, 0.44, 0]} castShadow><boxGeometry args={[0.52, 0.11, 0.52]} /><meshStandardMaterial color="#4b5961" roughness={0.8} /></mesh>
          <mesh position={[0, 0.77, 0.2]} castShadow><boxGeometry args={[0.52, 0.58, 0.1]} /><meshStandardMaterial color="#4b5961" roughness={0.8} /></mesh>
        </group>
      )))}
    </group>
  );
}

function Kitchen({ anchor }: { anchor: FurnitureAnchor }) {
  const island = anchor.kind === 'kitchen-island';
  const width = anchor.width ?? (island ? 3.2 : 3.8);
  const depth = anchor.depth ?? (island ? 1.2 : 0.7);
  return (
    <group position={[anchor.x, 0, anchor.z]} rotation={[0, anchor.rotationY ?? 0, 0]}>
      <mesh position={[0, 0.47, 0]} castShadow receiveShadow>
        <boxGeometry args={[width, 0.94, depth]} />
        <meshStandardMaterial color={island ? '#3f4a46' : '#d7d4ca'} roughness={0.58} />
      </mesh>
      <mesh position={[0, 0.98, 0]} castShadow receiveShadow>
        <boxGeometry args={[width + 0.1, 0.1, depth + 0.1]} />
        <meshStandardMaterial color="#ece9df" roughness={0.32} metalness={0.1} />
      </mesh>
      {island && [-0.95, 0, 0.95].map((x) => (
        <mesh key={x} position={[x, 0.98, -0.08]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[0.14, 20]} />
          <meshBasicMaterial color="#1c2224" />
        </mesh>
      ))}
    </group>
  );
}

function BathroomFixture({ anchor }: { anchor: FurnitureAnchor }) {
  if (anchor.kind === 'bath-spa') {
    return <mesh position={[anchor.x, 0.38, anchor.z]} castShadow receiveShadow><boxGeometry args={[2.65, 0.72, 1.25]} /><meshStandardMaterial color="#f4f1e9" roughness={0.26} metalness={0.04} /></mesh>;
  }
  if (anchor.kind === 'shower') {
    return <mesh position={[anchor.x, 0.95, anchor.z]} castShadow><boxGeometry args={[1.35, 1.9, 1.35]} /><meshStandardMaterial color="#bde5ef" transparent opacity={0.26} roughness={0.08} /></mesh>;
  }
  if (anchor.kind === 'vanity') {
    return <group position={[anchor.x, 0, anchor.z]}><mesh position={[0, 0.45, 0]} castShadow><boxGeometry args={[1.8, 0.85, 0.55]} /><meshStandardMaterial color="#6f7b78" roughness={0.58} /></mesh><mesh position={[0, 0.91, 0]}><boxGeometry args={[1.9, 0.09, 0.64]} /><meshStandardMaterial color="#eeeae0" roughness={0.28} /></mesh></group>;
  }
  return null;
}

function LaundryUnit({ anchor }: { anchor: FurnitureAnchor }) {
  if (anchor.kind === 'drying-rack') {
    return <group position={[anchor.x, 0, anchor.z]} rotation={[0, anchor.rotationY ?? 0, 0]}>{[-0.6, 0, 0.6].map((offset) => <mesh key={offset} position={[offset, 1.35, 0]} rotation={[0, 0, Math.PI / 2]}><cylinderGeometry args={[0.035, 0.035, 1.8, 8]} /><meshStandardMaterial color="#94a3a5" metalness={0.65} roughness={0.28} /></mesh>)}</group>;
  }
  return <group position={[anchor.x, 0, anchor.z]}><mesh position={[0, 0.58, 0]} castShadow receiveShadow><boxGeometry args={[0.9, 1.16, 0.72]} /><meshStandardMaterial color="#e8e9e5" roughness={0.42} /></mesh><mesh position={[0, 0.6, -0.366]} rotation={[Math.PI / 2, 0, 0]}><torusGeometry args={[0.22, 0.055, 12, 24]} /><meshStandardMaterial color="#576164" metalness={0.5} roughness={0.3} /></mesh></group>;
}

function StoneFireplace({ anchor }: { anchor: FurnitureAnchor }) {
  const flameRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.PointLight>(null);

  useFrame((state) => {
    const pulse = 0.85 + Math.sin(state.clock.elapsedTime * 9) * 0.12 + Math.sin(state.clock.elapsedTime * 5) * 0.08;
    if (flameRef.current) flameRef.current.scale.set(0.9 + pulse * 0.18, pulse, 0.9 + pulse * 0.18);
    if (glowRef.current) glowRef.current.intensity = 3.2 + pulse * 1.4;
  });

  return (
    <group position={[anchor.x, 0, anchor.z]} rotation={[0, anchor.rotationY ?? 0, 0]}>
      <mesh position={[0, 1.05, 0.1]} castShadow receiveShadow><boxGeometry args={[3.2, 2.1, 0.72]} /><meshStandardMaterial color="#70604e" roughness={0.96} /></mesh>
      <mesh position={[0, 0.62, -0.29]}><boxGeometry args={[1.35, 1.05, 0.08]} /><meshStandardMaterial color="#171717" roughness={0.98} /></mesh>
      <mesh ref={flameRef} position={[0, 0.72, -0.4]} rotation={[0, Math.PI / 4, 0]}><coneGeometry args={[0.34, 1.08, 8]} /><meshBasicMaterial color="#ffb74d" transparent opacity={0.94} /></mesh>
      <pointLight ref={glowRef} position={[0, 1.1, -1.4]} color="#ff9d3f" intensity={4} distance={9} decay={2} />
      <mesh position={[0, 2.75, 0.1]} castShadow><boxGeometry args={[1.65, 3.1, 0.6]} /><meshStandardMaterial color="#665747" roughness={0.98} /></mesh>
    </group>
  );
}

function BalconyRailing() {
  return <group>{[-18, -12, -6, 0, 6, 12, 18].map((x) => <mesh key={x} position={[x, 0.6, 17.9]} castShadow><boxGeometry args={[0.08, 1.2, 0.08]} /><meshStandardMaterial color="#37464a" metalness={0.65} roughness={0.3} /></mesh>)}<mesh position={[0, 1.12, 17.9]}><boxGeometry args={[40, 0.08, 0.08]} /><meshStandardMaterial color="#37464a" metalness={0.65} roughness={0.3} /></mesh></group>;
}

function MountainBackdrop() {
  const geometry = useMemo(() => {
    const shape = new THREE.Shape();
    shape.moveTo(-60, -3);
    shape.lineTo(-60, 9);
    shape.lineTo(-42, 22);
    shape.lineTo(-26, 10);
    shape.lineTo(-10, 28);
    shape.lineTo(5, 12);
    shape.lineTo(22, 24);
    shape.lineTo(42, 9);
    shape.lineTo(60, 18);
    shape.lineTo(60, -3);
    shape.closePath();
    return new THREE.ShapeGeometry(shape);
  }, []);

  return <group position={[0, 0, 42]} rotation={[0, Math.PI, 0]}><mesh geometry={geometry}><meshStandardMaterial color="#71949c" roughness={1} /></mesh><mesh position={[0, 0, 0.1]} geometry={geometry} scale={[0.9, 0.72, 1]}><meshStandardMaterial color="#d9e4e2" roughness={1} transparent opacity={0.72} /></mesh></group>;
}

function Furnishing({ anchor }: { anchor: FurnitureAnchor }) {
  switch (anchor.kind) {
    case 'queen-bed':
    case 'split-king-bed':
    case 'trundle-bed': return <Bed anchor={anchor} />;
    case 'bunk-bed': return <Bed anchor={anchor} bunk />;
    case 'wardrobe': return <mesh position={[anchor.x, 1.15, anchor.z]} rotation={[0, anchor.rotationY ?? 0, 0]} castShadow receiveShadow><boxGeometry args={[2.1, 2.3, 0.65]} /><meshStandardMaterial color="#67513d" roughness={0.68} /></mesh>;
    case 'sofa': return <Sofa anchor={anchor} />;
    case 'armchair': return <Sofa anchor={{ ...anchor, width: 1.1 }} />;
    case 'fireplace': return <StoneFireplace anchor={anchor} />;
    case 'kitchen-island':
    case 'kitchen-counter': return <Kitchen anchor={anchor} />;
    case 'dining-table': return <DiningTable anchor={anchor} />;
    case 'washer':
    case 'dryer':
    case 'drying-rack': return <LaundryUnit anchor={anchor} />;
    case 'bath-spa':
    case 'shower':
    case 'vanity': return <BathroomFixture anchor={anchor} />;
    case 'toilet': return <mesh position={[anchor.x, 0.38, anchor.z]} castShadow><boxGeometry args={[0.75, 0.76, 1.05]} /><meshStandardMaterial color="#f4f1e9" roughness={0.25} /></mesh>;
    case 'balcony-seating': return <Sofa anchor={{ ...anchor, width: 3.8 }} />;
  }
}

function CameraController({ preset, cameraRef }: { preset: CameraPreset; cameraRef: ForwardedRef<WoodsmokeTwinCameraController> }) {
  const controlsRef = useRef<OrbitControlsHandle | null>(null);
  const presetRef = useRef(preset);
  const lastPresetId = useRef<string | null>(null);
  const fromPosition = useRef(new THREE.Vector3());
  const fromTarget = useRef(new THREE.Vector3());
  const toPosition = useRef(new THREE.Vector3(...preset.position));
  const toTarget = useRef(new THREE.Vector3(...preset.target));
  const progress = useRef(1);
  const animating = useRef(false);
  presetRef.current = preset;

  const animateTo = (position: THREE.Vector3, target: THREE.Vector3) => {
    const controls = controlsRef.current;
    if (!controls) return;
    fromPosition.current.copy(controls.object.position);
    fromTarget.current.copy(controls.target);
    toPosition.current.copy(position);
    toTarget.current.copy(target);
    progress.current = 0;
    animating.current = true;
  };

  useImperativeHandle(cameraRef, () => ({
    zoomIn: () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const offset = controls.object.position.clone().sub(controls.target);
      const distance = THREE.MathUtils.clamp(offset.length() * 0.8, 8, 90);
      animateTo(controls.target.clone().addScaledVector(offset.normalize(), distance), controls.target.clone());
    },
    zoomOut: () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const offset = controls.object.position.clone().sub(controls.target);
      const distance = THREE.MathUtils.clamp(offset.length() * 1.25, 8, 90);
      animateTo(controls.target.clone().addScaledVector(offset.normalize(), distance), controls.target.clone());
    },
    pan: (direction) => {
      const controls = controlsRef.current;
      if (!controls) return;
      const forward = controls.target.clone().sub(controls.object.position).setY(0).normalize();
      const right = new THREE.Vector3().setFromMatrixColumn(controls.object.matrixWorld, 0).setY(0).normalize();
      const vector = direction === 'up' ? forward : direction === 'down' ? forward.negate() : direction === 'left' ? right.negate() : right;
      const distance = Math.max(controls.object.position.distanceTo(controls.target) * 0.1, 2.5);
      const target = controls.target.clone().addScaledVector(vector, distance);
      target.x = THREE.MathUtils.clamp(target.x, FLOOR_BOUNDS.minX, FLOOR_BOUNDS.maxX);
      target.z = THREE.MathUtils.clamp(target.z, FLOOR_BOUNDS.minZ, FLOOR_BOUNDS.maxZ);
      animateTo(controls.object.position.clone().add(target.clone().sub(controls.target)), target);
    },
    reset: () => animateTo(new THREE.Vector3(...presetRef.current.position), new THREE.Vector3(...presetRef.current.target)),
  }));

  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls || lastPresetId.current === null || lastPresetId.current === preset.id) return;
    animateTo(new THREE.Vector3(...preset.position), new THREE.Vector3(...preset.target));
    lastPresetId.current = preset.id;
  }, [preset]);

  useFrame((_, delta) => {
    const controls = controlsRef.current;
    if (!controls) return;
    if (lastPresetId.current === null) {
      controls.object.position.set(...presetRef.current.position);
      controls.target.set(...presetRef.current.target);
      controls.update();
      lastPresetId.current = presetRef.current.id;
      return;
    }
    if (!animating.current) return;
    controls.enabled = false;
    progress.current = Math.min(1, progress.current + delta * 1.7);
    const eased = 1 - Math.pow(1 - progress.current, 3);
    controls.object.position.lerpVectors(fromPosition.current, toPosition.current, eased);
    controls.target.lerpVectors(fromTarget.current, toTarget.current, eased);
    controls.update();
    if (progress.current >= 1) {
      animating.current = false;
      controls.enabled = true;
    }
  });

  return <OrbitControls ref={controlsRef as never} makeDefault enableDamping dampingFactor={0.08} minDistance={8} maxDistance={90} minPolarAngle={0.12} maxPolarAngle={Math.PI / 2.03} target={preset.target} />;
}

export const WoodsmokeTwinScene = forwardRef<WoodsmokeTwinCameraController, WoodsmokeTwinSceneProps>(function WoodsmokeTwinScene({ cameraPreset, selectedRoomId = null, onSelect, onHoverChange, children }, cameraRef) {
  const [hoveredRoomId, setHoveredRoomId] = useState<RoomId | null>(null);

  useEffect(() => () => {
    document.body.style.cursor = 'default';
  }, []);

  const selectRoom = (room: RoomDef | null) => onSelect(room);
  const handleHover = (room: RoomDef | null) => {
    setHoveredRoomId(room?.id ?? null);
    onHoverChange?.(room);
  };

  return (
    <Canvas shadows dpr={[1, 2]} className="touch-none" style={{ width: '100%', height: '100%', touchAction: 'none' }} camera={{ position: cameraPreset.position, fov: 45, near: 0.1, far: 180 }} onPointerMissed={() => selectRoom(null)} gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}>
      <color attach="background" args={['#9dbbc5']} />
      <fog attach="fog" args={['#9dbbc5', 46, 115]} />
      <Sky distance={450000} sunPosition={[-45, 28, 30]} inclination={0.58} azimuth={0.34} />
      <ambientLight intensity={0.62} color="#ffe7c1" />
      <hemisphereLight args={['#d8edf1', '#665c4f', 0.52]} />
      <directionalLight position={[-34, 46, 24]} intensity={2.1} color="#ffe8be" castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} shadow-camera-left={-42} shadow-camera-right={42} shadow-camera-top={42} shadow-camera-bottom={-42} />
      <mesh position={[0, -0.08, 1]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow><planeGeometry args={[76, 66]} /><meshStandardMaterial color="#64747a" roughness={1} /></mesh>
      {ROOMS.map((room) => <RoomFloor key={room.id} room={room} isSelected={selectedRoomId === room.id || hoveredRoomId === room.id} onSelect={selectRoom} onHoverChange={handleHover} />)}
      {STRUCTURAL_WALLS.map((wall) => <StructuralWall key={wall.id} wall={wall} />)}
      {FURNITURE_ANCHORS.map((anchor) => <Furnishing key={anchor.id} anchor={anchor} />)}
      <BalconyRailing />
      <MountainBackdrop />
      {children}
      <CameraController preset={cameraPreset} cameraRef={cameraRef} />
    </Canvas>
  );
});

export function isWoodsmokeWebGLAvailable(): boolean {
  return checkWebGLCapability();
}

export { CAMERA_PRESETS };
